import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { CategoryModalComponent } from '../../core/components/category-modal/category-modal.component';
import { ConfirmModalComponent } from '../../core/components/confirm-modal/confirm-modal.component';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';
import { SessionService } from '../../core/services/session.service';
import { AdminCategoriesService } from '../../core/services/admin-categories.service';

interface Category {
    id: string;
    categoryName: string;
    legacyId?: number;
}

@Component({
    selector: 'app-categories',
    imports: [CommonModule, FormsModule, FontAwesomeModule, NgbTooltip, MenuBarComponent, FooterComponent, AdminNavComponent, NgxTgModule],
    templateUrl: './categories.component.html',
    styleUrl: './categories.component.scss',
})
export class CategoriesComponent implements OnInit {
    categories: Category[] = [];
    loading = false;

    /** Frontend-only search over the loaded categories. */
    search = '';

    editingId: string | null = null;
    editValue = '';

    readonly faPen = faPen;
    readonly faTrash = faTrash;

    constructor(
        private categoriesService: AdminCategoriesService,
        private session: SessionService,
        private router: Router,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private i18n: TranslateService,
    ) { }

    ngOnInit(): void {
        if (!this.session.isLoggedIn() || !this.session.isAdmin()) {
            this.router.navigate(['/']);
            return;
        }
        this.load();
    }

    get filteredCategories(): Category[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.categories;
        return this.categories.filter((c) => (c.categoryName || '').toLowerCase().includes(q));
    }

    load(): void {
        this.loading = true;
        this.categoriesService.list().subscribe({
            next: (res) => {
                this.categories = res?.categories ?? [];
                this.loading = false;
            },
            error: () => {
                this.categories = [];
                this.loading = false;
            },
        });
    }

    openAddModal(): void {
        const ref = this.modalService.open(CategoryModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        ref.result.then(
            (name: string) => {
                if (!name) return;
                this.categoriesService.create(name).subscribe({
                    next: () => {
                        this.toastr.success(this.i18n.instant('categories_toast_created'));
                        this.load();
                    },
                    error: () => this.toastr.error(this.i18n.instant('categories_toast_create_error')),
                });
            },
            () => { /* dismissed */ },
        );
    }

    startEdit(c: Category): void {
        this.editingId = c.id;
        this.editValue = c.categoryName;
    }

    cancelEdit(): void {
        this.editingId = null;
        this.editValue = '';
    }

    saveEdit(c: Category): void {
        const name = this.editValue.trim();
        if (!name) return;
        this.categoriesService.update(c.id, name).subscribe({
            next: () => {
                this.editingId = null;
                this.toastr.success(this.i18n.instant('categories_toast_updated'));
                this.load();
            },
            error: () => this.toastr.error(this.i18n.instant('categories_toast_update_error')),
        });
    }

    remove(c: Category): void {
        const ref = this.modalService.open(ConfirmModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.title = this.i18n.instant('categories_confirm_delete_title');
        ref.componentInstance.message = this.i18n.instant('categories_confirm_delete_message');
        ref.componentInstance.subject = c.categoryName;
        ref.componentInstance.confirmLabel = this.i18n.instant('admin_action_delete');
        ref.componentInstance.cancelLabel = this.i18n.instant('admin_action_cancel');
        ref.result.then(
            () => {
                this.categoriesService.delete(c.id).subscribe({
                    next: () => {
                        this.toastr.success(this.i18n.instant('categories_toast_deleted'));
                        this.load();
                    },
                    error: () => this.toastr.error(this.i18n.instant('categories_toast_delete_error')),
                });
            },
            () => { /* dismissed */ },
        );
    }
}
